"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formattedPhoneNumber = exports.DEFAULT_TOKEN = void 0;
const COUNTRY_CODES = {
    NORWEGIAN: "47",
};
exports.DEFAULT_TOKEN = "N/A";
/**
 * Formats Norwegian numbers based on whether they are mobile or landline.
 * Mobile numbers follow a "XXX XX XXX" pattern, while landline numbers use a "XX XX XX XX" pattern.
 *
 * @privateRemarks
 * This function is not intended for public usage. Use formattedPhoneNumber instead
 *
 * @internal
 * @param {string} nationalNumber - The phone number to format without the country code.
 * @returns {string} - The formatted phone number.
 */
const formatNorwegian = (nationalNumber) => {
    if (nationalNumber.length !== 8)
        return nationalNumber;
    const firstDigit = nationalNumber.charAt(0);
    const isMobile = ["4", "9", "8"].includes(firstDigit);
    return isMobile
        ? `${nationalNumber.slice(0, 3)} ${nationalNumber.slice(3, 5)} ${nationalNumber.slice(5)}`
        : `${nationalNumber.slice(0, 2)} ${nationalNumber.slice(2, 4)} ${nationalNumber.slice(4, 6)} ${nationalNumber.slice(6)}`;
};
/**
 * Formats a phone number based on the provided country code. This method is currently
 * set up to handle Norwegian numbers only, but it is designed to be easily
 * expandable to accommodate additional formatting rules for other countries.
 *
 * The DEFAULT_TOKEN is returned if the argument is invalid.
 *
 * __Expanding Functionality__
 * To add support for other countries:
 * 1. Extend the `COUNTRY_CODES` object with the new country code.
 * 2. Create a new function similar to `formatNorwegian` tailored to the new country's formatting rules.
 * 3. In the main `formattedPhoneNumber` function, add an additional conditional branch to handle the new country code.
 *
 * __Adding Tests__
 * When expanding, remember to add corresponding unit tests for each new country's formatting logic.
 * This ensures the formatter remains reliable and works as expected across different scenarios.
 * Tests should cover both typical cases and edge cases (e.g., input validation, unexpected formats).
 *
 * @param {IPhoneNumberParams} params - Object containing the national number and the country code.
 * @returns {string} - The formatted phone number or the original number if no specific formatting is applied.
 */
const formattedPhoneNumber = (arg) => {
    if (arg == null) {
        return exports.DEFAULT_TOKEN;
    }
    const { nationalNumber, countryCode } = arg;
    if (!nationalNumber || !nationalNumber?.length) {
        return exports.DEFAULT_TOKEN;
    }
    if (countryCode === COUNTRY_CODES.NORWEGIAN) {
        return `+${countryCode} ${formatNorwegian(nationalNumber)}`;
    }
    if (countryCode) {
        return `+${countryCode} ${nationalNumber}`;
    }
    return nationalNumber;
};
exports.formattedPhoneNumber = formattedPhoneNumber;
